// @ds/data/queryHelpers — pure helpers for Query<T> dispatch.
//
// Three concerns:
//   1. `deriveSelect`  — walk a schema's picked fields' deps and assemble
//                        a Supabase select string. Delegates to the existing
//                        `buildSelectString` helper for the actual string
//                        assembly so output matches table-side queries.
//   2. `composeFilters` — combine consumer filters with the schema's
//                        softDelete default (auto-appends `is_deleted=false`
//                        when `__meta.softDelete` is set).
//   3. `detectInnerJoins` — scan filters for dotted paths (`"a.b"`) and
//                        return the parent-table names that need `!inner`
//                        joins (`["a"]`).
//
// No hooks, no React. Pure functions, fully testable in isolation.

import type { CrudFilter } from "@refinedev/core";
import type { ListColumnSpec } from "@ds/tables/types";
import { buildSelectString } from "@ds/tables/helpers/buildSelectString";
import { getSchemaMeta, type SchemaField } from "./schema";
import type { AnySchema } from "./query";

// ─── deriveSelect ───────────────────────────────────────────────────────────

/**
 * Walk picked-field deps + extras to produce the union of:
 *   - scalar fields to SELECT
 *   - relation joins to nest
 *   - inner joins implied by dotted-path filters (M:N traversal)
 *   - schema-level `permanentFields`
 *
 * Returns a PostgREST select string (e.g.
 * `"id,number,issue_date,issuer:issuer_id(id,short_name),
 * invoice_links!inner(invoice_id,suggestion,stale)"`). Returns `undefined`
 * when no deps are declared and no extras requested (let Supabase return
 * all columns).
 *
 * Delegates to `buildSelectString` (the existing canonical assembler) so
 * the output shape matches every other table/list fetch in the app.
 */
export function deriveSelect<T>(
  schema: AnySchema<T>,
  pickedFieldKeys: ReadonlyArray<string>,
  also: ReadonlyArray<string> = [],
  filters: CrudFilter[] = [],
): string | undefined {
  // `__meta` is attached at runtime by defineSchema but hidden from the
  // static type (see AnySchema docs). Read it via getSchemaMeta which
  // throws if the schema was constructed via the deprecated zero-arg path.
  const meta = getSchemaMeta(schema);

  // Collect ALL keys we need fields/relations for:
  //   pickedFieldKeys ∪ also ∪ meta.permanentFields
  const keys = new Set<string>();
  for (const k of pickedFieldKeys) keys.add(k);
  for (const k of also) keys.add(k);
  for (const k of meta.permanentFields ?? []) keys.add(k);

  // Resolve each key to the underlying SchemaField (skip unknown — they
  // become plain scalar fields via permanentFields path below).
  const columns: ListColumnSpec<unknown, unknown>[] = [];
  const plainScalarPermanents: string[] = [];

  for (const key of keys) {
    const field = schema[key] as SchemaField<T, unknown> | undefined;
    if (field && field.deps) {
      // SchemaField + deps → pass through as a column-shaped object so
      // buildSelectString can read .deps. The key drives the SELECT
      // column when no `deps.fields` is set; we add it explicitly below.
      columns.push({
        key,
        deps: field.deps,
      } as unknown as ListColumnSpec<unknown, unknown>);
      // If the field has no explicit `deps.fields` entry, the field key
      // itself is the scalar column to SELECT (the common case for plain
      // scalars like `name`, `issue_date`).
      if (!field.deps.fields || field.deps.fields.length === 0) {
        plainScalarPermanents.push(key);
      }
    } else if (field) {
      // Field exists but has no deps → treat the key itself as scalar.
      plainScalarPermanents.push(key);
    } else {
      // Unknown key (e.g. `also: ["currency"]` referring to a column the
      // schema doesn't carry a declarative field for) → treat as scalar.
      plainScalarPermanents.push(key);
    }
  }

  // Inner-join hints from dotted-path filters (M:N).
  columns.push(...innerJoinColumns(filters));

  // Permanent fields the schema author declared as always-fetch.
  const permanents = [
    ...(meta.permanentFields ?? []),
    ...plainScalarPermanents,
  ];

  return buildSelectString(columns, permanents);
}

// Build `!inner`-join column entries from dotted-path filters (`"table.col"`).
function innerJoinColumns(filters: CrudFilter[]): ListColumnSpec<unknown, unknown>[] {
  const out: ListColumnSpec<unknown, unknown>[] = [];
  for (const t of detectInnerJoins(filters)) {
    const entry = {
      key: `__inner_${t}`,
      deps: {
        relations: { [t]: [] as string[] },
        foreignKeys: { [t]: { foreignKey: `${t}!inner` } },
      },
    };
    for (const f of filters) {
      if ("field" in f && typeof f.field === "string" && f.field.startsWith(`${t}.`)) {
        const col = f.field.slice(t.length + 1);
        if (!entry.deps.relations[t].includes(col)) entry.deps.relations[t].push(col);
      }
    }
    out.push(entry as unknown as ListColumnSpec<unknown, unknown>);
  }
  return out;
}

/**
 * Object-based select derivation: build the PostgREST select from resolved
 * field OBJECTS' deps (not key→schema lookup), plus dotted-path inner joins
 * and the schema's permanentFields. Lets a sub-section render config-composed
 * columns (`pickRef`, `col`) that don't live in the schema.
 */
export function deriveSelectFromColumns<T>(
  schema: AnySchema<T>,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  columns: ReadonlyArray<{ deps?: any; key?: string }>,
  also: ReadonlyArray<string> = [],
  filters: CrudFilter[] = [],
): string | undefined {
  const meta = getSchemaMeta(schema);
  const cols = [
    ...columns,
    ...innerJoinColumns(filters),
  ] as unknown as ReadonlyArray<ListColumnSpec<unknown, unknown>>;
  const permanents = [...(meta.permanentFields ?? []), ...also];
  return buildSelectString(cols, permanents);
}

// ─── composeFilters ─────────────────────────────────────────────────────────

/**
 * Compose query filters with the schema's softDelete default. When
 * `schema.__meta.softDelete` is true, append the canonical
 * `is_deleted=false` permanent filter UNLESS the caller already filters
 * on `is_deleted` explicitly (escape hatch for "show me soft-deleted rows
 * too").
 */
export function composeFilters<T>(
  schema: AnySchema<T>,
  filters: CrudFilter[] | undefined,
): CrudFilter[] {
  const out = [...(filters ?? [])];
  // `__meta` is attached at runtime by defineSchema but hidden from the
  // static type (see AnySchema docs). Read it via getSchemaMeta which
  // throws if the schema was constructed via the deprecated zero-arg path.
  const meta = getSchemaMeta(schema);
  if (!meta.softDelete) return out;

  // Skip if caller already filters on is_deleted (any op).
  const hasIsDeleted = out.some(
    (f) => "field" in f && (f as { field?: string }).field === "is_deleted",
  );
  if (hasIsDeleted) return out;

  out.push({ field: "is_deleted", operator: "eq", value: false });
  return out;
}

// ─── detectInnerJoins ───────────────────────────────────────────────────────

/**
 * Scan filters for dotted-path field references (e.g.
 * `"invoice_links.invoice_id"`) and return the parent-table names that
 * need `!inner` joins. De-dupes. Order is the order of first appearance
 * in the filter list.
 *
 * Skips conjunction (`"and" | "or"`) filters — those carry nested
 * `CrudFilter[]` arrays that we'd need to recurse into for full coverage.
 * Most consumer filter lists are flat; recursing isn't worth the
 * complexity until a real use case demands it.
 */
export function detectInnerJoins(filters: CrudFilter[]): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const f of filters) {
    if (!("field" in f) || typeof f.field !== "string") continue;
    const dot = f.field.indexOf(".");
    if (dot <= 0) continue;
    const table = f.field.slice(0, dot);
    if (seen.has(table)) continue;
    seen.add(table);
    out.push(table);
  }
  return out;
}
